import dayjs from 'dayjs';
import { ScheduleException } from '../../services/scheduleApi';
import { minutesOfDay, minutesToHeight, minutesToTop } from './parts/laneMath';

/**
 * Client-side mirror of the backend `scheduleExceptionExpansion.expandExceptions`
 * (backend/src/services/scheduleExceptionExpansion.ts). Expands weekly-recurring
 * exceptions into concrete-dated occurrences within [rangeStart, rangeEnd]
 * (inclusive, 'YYYY-MM-DD') so a recurring lunch shows on every matching day.
 *
 * The weekly stride/interval rules MUST match the backend exactly, or the
 * calendar would paint blocks on days the availability engine does not block.
 * Non-recurring rows and non-weekly patterns pass through unchanged. Datetimes
 * stay in space-separated local 'YYYY-MM-DD HH:mm:ss' form — no ISO/UTC parsing.
 */
export function expandExceptionsClient(
  exceptions: ScheduleException[],
  rangeStart: string,
  rangeEnd: string,
): ScheduleException[] {
  const out: ScheduleException[] = [];
  const start = dayjs(rangeStart);
  const end = dayjs(rangeEnd);

  for (const ex of exceptions) {
    let pattern: any = null;
    if (ex.is_recurring && ex.recurrence_pattern) {
      const rp: any = ex.recurrence_pattern;
      if (typeof rp === 'string') {
        try {
          pattern = JSON.parse(rp);
        } catch {
          pattern = null;
        }
      } else {
        pattern = rp;
      }
    }
    if (!pattern || pattern.frequency !== 'weekly') {
      out.push(ex);
      continue;
    }
    const interval = Math.max(1, Number(pattern.interval) || 1);
    const anchor = dayjs(ex.start_datetime);
    const startTime = anchor.format('HH:mm:ss');
    const endAnchor = dayjs(ex.end_datetime);
    const durationMin = endAnchor.diff(anchor, 'minute');

    // First occurrence on/after rangeStart sharing the anchor's weekday + interval stride.
    let cur = anchor;
    if (cur.isBefore(start)) {
      const weeksApart = Math.ceil(start.diff(cur, 'week', true) / interval) * interval;
      cur = anchor.add(weeksApart, 'week');
      while (cur.isBefore(start, 'day')) cur = cur.add(interval, 'week');
    }
    while (!cur.isAfter(end, 'day')) {
      const occStart = `${cur.format('YYYY-MM-DD')} ${startTime}`;
      const occEnd = dayjs(occStart).add(durationMin, 'minute').format('YYYY-MM-DD HH:mm:ss');
      out.push({ ...ex, start_datetime: occStart, end_datetime: occEnd });
      cur = cur.add(interval, 'week');
    }
  }
  return out;
}

/** Build a space-separated local 'YYYY-MM-DD HH:mm:ss' from a day + minute-of-day. */
export function slotStartLocal(dateStr: string, minuteOfDay: number): string {
  const clamped = Math.max(0, Math.min(minuteOfDay, 24 * 60 - 1));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dateStr} ${pad(h)}:${pad(m)}:00`;
}

export interface PositionedException {
  exception: ScheduleException;
  /** The day this positioned slice falls on ('YYYY-MM-DD'). */
  date: string;
  top: number;
  height: number;
}

/**
 * Expand + bucket exceptions into per-day, per-lane-track positioned slices
 * using the SAME open/close/track lane math the grids use. A block spanning
 * multiple days is split into one slice per covered day (each clamped to that
 * day's open/close window by the lane math), so a multi-day time-off paints on
 * every day it covers, not just the first.
 */
export function bucketExceptionsByDate(
  exceptions: ScheduleException[],
  rangeStart: string,
  rangeEnd: string,
  openMin: number,
  closeMin: number,
  trackHeight: number,
  minHeight = 12,
): Map<string, PositionedException[]> {
  const expanded = expandExceptionsClient(exceptions, rangeStart, rangeEnd);
  const buckets = new Map<string, PositionedException[]>();
  const rangeStartDay = dayjs(rangeStart);
  const rangeEndDay = dayjs(rangeEnd);

  for (const ex of expanded) {
    const s = dayjs(ex.start_datetime);
    const e = dayjs(ex.end_datetime);
    if (!s.isValid() || !e.isValid()) continue;

    const lastDay = e.startOf('day');
    for (let cur = s.startOf('day'); !cur.isAfter(lastDay, 'day'); cur = cur.add(1, 'day')) {
      // Only paint days inside the requested range.
      if (cur.isBefore(rangeStartDay, 'day') || cur.isAfter(rangeEndDay, 'day')) continue;

      const isFirstDay = cur.isSame(s, 'day');
      const isLastDay = cur.isSame(e, 'day');
      const startMin = isFirstDay ? minutesOfDay(s) : 0;
      const endMin = isLastDay ? minutesOfDay(e) : 24 * 60;
      // A block wholly outside the visible window on this day (e.g. a 6am-7am
      // break before the 8a open) collapses to nothing — skip it.
      if (endMin <= openMin || startMin >= closeMin) continue;

      const dateStr = cur.format('YYYY-MM-DD');
      const top = minutesToTop(startMin, openMin, closeMin, trackHeight);
      const height = minutesToHeight(startMin, endMin, openMin, closeMin, trackHeight, minHeight);
      const list = buckets.get(dateStr) || [];
      list.push({ exception: ex, date: dateStr, top, height });
      buckets.set(dateStr, list);
    }
  }
  return buckets;
}
